"use client";

import React, { useEffect, useState } from "react";
import { ArrowLeft, FileCheck2, Loader2, Play, Plus, Save, Trash2 } from "lucide-react";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { getFY_YEAR } from "@/utils/sharepoint-env";
import { formatLeaseDialogCell } from "@/utils/lease-dialog-format";

interface LeaseAccountingExceptionIndASProps {
  onBack?: () => void;
  setActiveSection?: (section: string) => void;
}

interface PaymentDetailRow {
  payment_classification: string;
  payment_type: string;
  payment_amount: number;
  no_of_months: number;
  interval_text: string;
  increment_percent_text: string;
  month_of_first_payment: number;
}

interface LeaseRow {
  id: string;
  lease_name: string;
  commencement_date: string;
  lease_type: string;
  lease_period_months: number;
  number_of_assets: number;
  payment_details: PaymentDetailRow[];
}

const FILE_NAME = "Execution_LEASE_AccExpInp.json";
const OUTPUT_FILE_NAME = "Execution_LEASE_AccExpOut.json";

interface OutputLease {
  lease_info?: Record<string, unknown>;
  payment_details?: Array<Record<string, unknown>>;
  schedule?: Array<Record<string, unknown>>;
}

const NON_APPLICABLE_CLASSIFICATIONS = new Set([
  "Guaranteed Residual Value",
  "Purchase Price",
  "Penalty for Termination",
]);

const defaultPaymentRow = (): PaymentDetailRow => ({
  payment_classification: "",
  payment_type: "",
  payment_amount: 0,
  no_of_months: 0,
  interval_text: "",
  increment_percent_text: "",
  month_of_first_payment: 0,
});

export default function LeaseAccountingExceptionIndAS({ setActiveSection }: LeaseAccountingExceptionIndASProps) {
  const [leases, setLeases] = useState<LeaseRow[]>([]);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [isRunning, setIsRunning] = useState(false);
  const [outputLeases, setOutputLeases] = useState<OutputLease[]>([]);
  const [selectedOutputLease, setSelectedOutputLease] = useState<OutputLease | null>(null);
  const [isOutputDialogOpen, setIsOutputDialogOpen] = useState(false);

  const loadOutputData = async () => {
    try {
      const result = await (window as any).sharePointAPI?.downloadClientJson?.(OUTPUT_FILE_NAME);
      if (result?.success && Array.isArray(result?.data?.leases)) {
        setOutputLeases(result.data.leases);
      } else {
        setOutputLeases([]);
      }
    } catch (error) {
      console.warn("[LeaseAccountingExceptionIndAS] Failed to load output:", error);
      setOutputLeases([]);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(FILE_NAME);
        if (result?.success && Array.isArray(result?.data?.leases)) {
          const loaded: LeaseRow[] = result.data.leases.map((lease: any, index: number) => ({
            id: `acc_exp_${Date.now()}_${index}`,
            lease_name: lease.lease_name || "",
            commencement_date: toInputDate(lease.commencement_date || ""),
            lease_type: lease.lease_type || "",
            lease_period_months: Number(lease.lease_period_months) || 0,
            number_of_assets: Number(lease.number_of_assets) || 0,
            payment_details:
              Array.isArray(lease.payment_details) && lease.payment_details.length > 0
                ? lease.payment_details.map((p: any) => ({
                    payment_classification: p.payment_classification || "",
                    payment_type: p.payment_type || "",
                    payment_amount: Number(p.payment_amount) || 0,
                    no_of_months: Number(p.no_of_months) || 0,
                    interval_text: Array.isArray(p.increment?.interval_months)
                      ? p.increment.interval_months.join(",")
                      : "",
                    increment_percent_text: Array.isArray(p.increment?.increment_percent)
                      ? p.increment.increment_percent.join(",")
                      : "",
                    month_of_first_payment: Number(p.month_of_first_payment) || 0,
                  }))
                : [defaultPaymentRow()],
          }));
          setLeases(loaded);
        }
      } catch (error) {
        console.warn("[LeaseAccountingExceptionIndAS] Failed to load data:", error);
      }
    };
    loadData();
    loadOutputData();
  }, []);

  const addLease = () => {
    setLeases((prev) => [
      ...prev,
      {
        id: `acc_exp_${Date.now()}`,
        lease_name: "",
        commencement_date: "",
        lease_type: "",
        lease_period_months: 0,
        number_of_assets: 0,
        payment_details: [defaultPaymentRow()],
      },
    ]);
  };

  const removeLease = (id: string) => setLeases((prev) => prev.filter((l) => l.id !== id));

  const updateLease = (id: string, field: keyof LeaseRow, value: string | number) => {
    setLeases((prev) => prev.map((l) => (l.id === id ? { ...l, [field]: value } : l)));
  };

  const addPaymentDetail = (leaseId: string) => {
    setLeases((prev) =>
      prev.map((l) =>
        l.id === leaseId ? { ...l, payment_details: [...l.payment_details, defaultPaymentRow()] } : l,
      ),
    );
  };

  const removePaymentDetail = (leaseId: string, index: number) => {
    setLeases((prev) =>
      prev.map((l) => {
        if (l.id !== leaseId) return l;
        const next = l.payment_details.filter((_, i) => i !== index);
        return { ...l, payment_details: next.length ? next : [defaultPaymentRow()] };
      }),
    );
  };

  const updatePaymentDetail = (
    leaseId: string,
    rowIndex: number,
    field: keyof PaymentDetailRow,
    value: string | number,
  ) => {
    setLeases((prev) =>
      prev.map((l) =>
        l.id === leaseId
          ? {
              ...l,
              payment_details: l.payment_details.map((row, i) =>
                i === rowIndex ? { ...row, [field]: value } : row,
              ),
            }
          : l,
      ),
    );
  };

  const saveData = async (): Promise<boolean> => {
    if (saveStatus === "saving") return false;
    setSaveStatus("saving");
    try {
      const fyYear = getFY_YEAR();
      if (!fyYear) throw new Error("FY_YEAR is not configured. Please select an engagement.");

      const payload = {
        leases: leases.map((lease) => ({
          lease_name: lease.lease_name || "",
          commencement_date: lease.commencement_date ? fromInputDate(lease.commencement_date) : "",
          lease_type: lease.lease_type || "",
          lease_period_months: Number(lease.lease_period_months) || 0,
          number_of_assets: Number(lease.number_of_assets) || 0,
          payment_details: lease.payment_details.map((p) => {
            const nonApplicable = NON_APPLICABLE_CLASSIFICATIONS.has(p.payment_classification);
            return {
              payment_classification: p.payment_classification || "",
              payment_type: p.payment_type || "",
              payment_amount: Number(p.payment_amount) || 0,
              no_of_months: nonApplicable ? 0 : Number(p.no_of_months) || 0,
              increment: nonApplicable
                ? { interval_months: [], increment_percent: [] }
                : {
                    interval_months: parseCommaSeparatedNumbers(p.interval_text),
                    increment_percent: parseCommaSeparatedNumbers(p.increment_percent_text),
                  },
              month_of_first_payment: nonApplicable ? 0 : Number(p.month_of_first_payment) || 0,
            };
          }),
        })),
      };

      const result = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: btoa(JSON.stringify(payload, null, 2)),
        fileName: FILE_NAME,
        referenceValue: "",
        folderName: "juggernaut",
        fyYear,
        sectionList: ["Execution_LEASE_AccExpInp"],
      });

      if (!result?.success) throw new Error(result?.error || "Failed to save file");
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 3000);
      return true;
    } catch (error) {
      console.error("[LeaseAccountingExceptionIndAS] Failed to save:", error);
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 3000);
      return false;
    }
  };

  const runLeaseAccountingException = async () => {
    if (isRunning || saveStatus === "saving") return;
    setIsRunning(true);
    try {
      const saved = await saveData();
      if (!saved) {
        throw new Error("Please fix save errors before running.");
      }
      const result = await (window as any).payroll?.run?.("execute_lease_accounting_exception", {
        inputFiles: [],
        options: {
          reference_value: "Lease Accounting Exception",
        },
      });
      if (!result?.ok) {
        throw new Error(result?.error || "Failed to run Lease Accounting Exception");
      }
      await loadOutputData();
    } catch (error) {
      console.error("[LeaseAccountingExceptionIndAS] Run failed:", error);
      alert(`Failed to run: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setIsRunning(false);
    }
  };

  const openLeaseOutput = (leaseName: string) => {
    const matched = outputLeases.find(
      (item) => String(item?.lease_info?.["Name of Lease"] || "").trim() === String(leaseName || "").trim(),
    );
    if (!matched) {
      alert("Output not found for this lease. Run the procedure first.");
      return;
    }
    setSelectedOutputLease(matched);
    setIsOutputDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Lease Accounting Exception</h2>
          <p className="text-gray-400">Enter lease details and payment information</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <Button
            onClick={runLeaseAccountingException}
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            disabled={isRunning || saveStatus === "saving"}
          >
            {isRunning ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Running...
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                Run
              </>
            )}
          </Button>
          <Button onClick={() => void saveData()} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" disabled={saveStatus === "saving"}>
            {saveStatus === "saving" ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save
              </>
            )}
          </Button>
          {saveStatus === "saved" && <span className="text-sm text-green-400">Saved!</span>}
          <Button variant="outline" onClick={() => setActiveSection?.("ll-substantive")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
      </div>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-white">
            <div className="flex items-center gap-3">
              <FileCheck2 className="h-5 w-5 text-blue-500" />
              Lease Accounting Exception
            </div>
            <Button onClick={addLease} size="sm" className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-2" />
              Add Lease
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {leases.length === 0 ? (
            <div className="text-center py-8 text-white/60">No leases added yet. Click &quot;Add Lease&quot; to get started.</div>
          ) : (
            leases.map((lease, leaseIndex) => (
              <div key={lease.id} className="border border-white/10 rounded-lg p-4 bg-white/5 space-y-4">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <h4 className="text-white font-medium">Lease #{leaseIndex + 1}</h4>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-white/20 text-white"
                      onClick={() => openLeaseOutput(lease.lease_name)}
                      data-allow-archival-edit={true}
                    >
                      Open
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      onClick={() => removeLease(lease.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white text-sm">Name of Lease</Label>
                    <Input
                      value={lease.lease_name}
                      onChange={(e) => updateLease(lease.id, "lease_name", e.target.value)}
                      className="border-white/10 bg-black/40 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white text-sm">Lease Commencement Date</Label>
                    <Input
                      type="date"
                      value={lease.commencement_date}
                      onChange={(e) => updateLease(lease.id, "commencement_date", e.target.value)}
                      className="border-white/10 bg-black/40 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white text-sm">Lease Type</Label>
                    <Select value={lease.lease_type} onValueChange={(v) => updateLease(lease.id, "lease_type", v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Short Term Lease">Short Term Lease</SelectItem>
                        <SelectItem value="Low-Value Lease">Low-Value Lease</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white text-sm">Lease Period (in months)</Label>
                    <Input
                      type="number"
                      value={lease.lease_period_months}
                      onChange={(e) => updateLease(lease.id, "lease_period_months", parseInt(e.target.value) || 0)}
                      className="border-white/10 bg-black/40 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white text-sm">Number of Assets</Label>
                    <Input
                      type="number"
                      value={lease.number_of_assets}
                      onChange={(e) => updateLease(lease.id, "number_of_assets", parseInt(e.target.value) || 0)}
                      className="border-white/10 bg-black/40 text-white"
                    />
                  </div>
                </div>

                <div className="border border-white/10 rounded-lg p-3 bg-black/20 space-y-3">
                  <div className="flex items-center justify-between">
                    <h5 className="text-white font-medium">Payment details</h5>
                    <Button onClick={() => addPaymentDetail(lease.id)} size="sm" className="bg-green-600 hover:bg-green-700">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Row
                    </Button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-white/10">
                      <thead>
                        <tr className="bg-black/60">
                          <th className="border border-white/10 px-3 py-2 text-left text-white text-sm">Payment Classification</th>
                          <th className="border border-white/10 px-3 py-2 text-left text-white text-sm">Payment Type</th>
                          <th className="border border-white/10 px-3 py-2 text-left text-white text-sm">Payment Amount (per month/year)</th>
                          <th className="border border-white/10 px-3 py-2 text-left text-white text-sm">No. of months</th>
                          <th className="border border-white/10 px-3 py-2 text-left text-white text-sm">Interval of Increment</th>
                          <th className="border border-white/10 px-3 py-2 text-left text-white text-sm">Increment %</th>
                          <th className="border border-white/10 px-3 py-2 text-left text-white text-sm">Month of First Payment</th>
                          <th className="border border-white/10 px-3 py-2 text-left text-white text-sm">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lease.payment_details.map((row, rowIndex) => {
                          const nonApplicable = NON_APPLICABLE_CLASSIFICATIONS.has(row.payment_classification);
                          return (
                            <tr key={`${lease.id}_${rowIndex}`} className="hover:bg-white/5">
                              <td className="border border-white/10 px-2 py-2">
                                <Select
                                  value={row.payment_classification}
                                  onValueChange={(v) => updatePaymentDetail(lease.id, rowIndex, "payment_classification", v)}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Fixed Payment">Fixed Payment</SelectItem>
                                    <SelectItem value="Variable Payment">Variable Payment</SelectItem>
                                    <SelectItem value="Guaranteed Residual Value">Guaranteed Residual Value</SelectItem>
                                    <SelectItem value="Purchase Price">Purchase Price</SelectItem>
                                    <SelectItem value="Penalty for Termination">Penalty for Termination</SelectItem>
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="border border-white/10 px-2 py-2">
                                <Input
                                  value={row.payment_type}
                                  onChange={(e) => updatePaymentDetail(lease.id, rowIndex, "payment_type", e.target.value)}
                                  className="border-white/10 bg-black/40 text-white"
                                />
                              </td>
                              <td className="border border-white/10 px-2 py-2">
                                <Input
                                  type="number"
                                  value={row.payment_amount}
                                  onChange={(e) =>
                                    updatePaymentDetail(lease.id, rowIndex, "payment_amount", parseFloat(e.target.value) || 0)
                                  }
                                  className="border-white/10 bg-black/40 text-white"
                                />
                              </td>
                              <td className="border border-white/10 px-2 py-2">
                                <Input
                                  type="number"
                                  value={row.no_of_months}
                                  disabled={nonApplicable}
                                  onChange={(e) =>
                                    updatePaymentDetail(lease.id, rowIndex, "no_of_months", parseInt(e.target.value) || 0)
                                  }
                                  className={`border-white/10 bg-black/40 text-white ${nonApplicable ? "opacity-50 cursor-not-allowed" : ""}`}
                                />
                              </td>
                              <td className="border border-white/10 px-2 py-2">
                                <Input
                                  value={row.interval_text}
                                  disabled={nonApplicable}
                                  placeholder="e.g. 13,37,49"
                                  onChange={(e) => updatePaymentDetail(lease.id, rowIndex, "interval_text", e.target.value)}
                                  className={`border-white/10 bg-black/40 text-white ${nonApplicable ? "opacity-50 cursor-not-allowed" : ""}`}
                                />
                              </td>
                              <td className="border border-white/10 px-2 py-2">
                                <Input
                                  value={row.increment_percent_text}
                                  disabled={nonApplicable}
                                  placeholder="e.g. 5,6,7"
                                  onChange={(e) =>
                                    updatePaymentDetail(lease.id, rowIndex, "increment_percent_text", e.target.value)
                                  }
                                  className={`border-white/10 bg-black/40 text-white ${nonApplicable ? "opacity-50 cursor-not-allowed" : ""}`}
                                />
                              </td>
                              <td className="border border-white/10 px-2 py-2">
                                <Input
                                  type="number"
                                  value={row.month_of_first_payment}
                                  disabled={nonApplicable}
                                  onChange={(e) =>
                                    updatePaymentDetail(
                                      lease.id,
                                      rowIndex,
                                      "month_of_first_payment",
                                      parseInt(e.target.value) || 0,
                                    )
                                  }
                                  className={`border-white/10 bg-black/40 text-white ${nonApplicable ? "opacity-50 cursor-not-allowed" : ""}`}
                                />
                              </td>
                              <td className="border border-white/10 px-2 py-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                  onClick={() => removePaymentDetail(lease.id, rowIndex)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-white/70 text-sm">
                    Note: Enter interval months and increment percentages as comma-separated values (e.g. 13,37,49 and 5,6,7). For
                    Guaranteed Residual Value, Purchase Price, and Penalty for Termination, the increment and month fields do not
                    apply.
                  </p>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Dialog open={isOutputDialogOpen} onOpenChange={setIsOutputDialogOpen}>
        <DialogContent className="max-w-[95vw] w-[95vw] h-[90vh] bg-black border-white/20 text-white overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              Lease Accounting Exception Output — {String(selectedOutputLease?.lease_info?.["Name of Lease"] ?? "")}
            </DialogTitle>
          </DialogHeader>
          <div className="h-full overflow-auto space-y-4 pr-1">
            {!selectedOutputLease ? (
              <div className="text-white/60">No output selected.</div>
            ) : (
              <>
                <div className="space-y-2">
                  <h3 className="text-white text-sm font-semibold">Lease info</h3>
                  <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-white/10">
                    <thead>
                      <tr className="bg-black/60">
                        {Object.keys(selectedOutputLease.lease_info || {}).map((key) => (
                          <th key={key} className="border border-white/10 px-3 py-2 text-left text-white text-sm">
                            {key}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="hover:bg-white/5">
                        {Object.keys(selectedOutputLease.lease_info || {}).map((key) => (
                          <td key={key} className="border border-white/10 px-3 py-2 text-white text-sm">
                            {formatLeaseDialogCell(selectedOutputLease.lease_info?.[key], key)}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                  </div>
                </div>

                {Array.isArray(selectedOutputLease.payment_details) && selectedOutputLease.payment_details.length > 0 ? (
                  <div className="space-y-2">
                    <h3 className="text-white text-sm font-semibold">Payment details</h3>
                    <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-white/10">
                      <thead>
                        <tr className="bg-black/60">
                          {(Object.keys(selectedOutputLease.payment_details[0] || {})).map((header) => (
                            <th key={header} className="border border-white/10 px-3 py-2 text-left text-white text-sm">
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(selectedOutputLease.payment_details || []).map((row, idx) => {
                          const headers = Object.keys(selectedOutputLease.payment_details![0] || {});
                          return (
                            <tr key={idx} className="hover:bg-white/5">
                              {headers.map((header) => (
                                <td key={`${idx}-${header}`} className="border border-white/10 px-3 py-2 text-white text-sm">
                                  {formatLeaseDialogCell(row[header], header)}
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    </div>
                  </div>
                ) : null}

                <div className="space-y-2">
                  <h3 className="text-white text-sm font-semibold">Schedule</h3>
                  <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-white/10">
                    <thead>
                      <tr className="bg-black/60">
                        {(selectedOutputLease.schedule?.length ? Object.keys(selectedOutputLease.schedule[0]) : []).map((header) => (
                          <th key={header} className="border border-white/10 px-3 py-2 text-left text-white text-sm whitespace-nowrap">
                            {header || " "}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedOutputLease.schedule || []).map((row, idx) => {
                        const headers = selectedOutputLease.schedule?.length ? Object.keys(selectedOutputLease.schedule[0]) : [];
                        return (
                          <tr key={idx} className="hover:bg-white/5">
                            {headers.map((header) => (
                              <td key={`${idx}-${header}`} className="border border-white/10 px-3 py-2 text-white text-sm whitespace-nowrap">
                                {formatLeaseDialogCell(row[header], header)}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function parseCommaSeparatedNumbers(value: string): number[] {
  if (!value || !value.trim()) return [];
  return value
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean)
    .map((v) => Number(v))
    .filter((v) => !Number.isNaN(v));
}

function toInputDate(value: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  }
  const parts = value.includes("/") ? value.split("/") : [];
  if (parts.length === 3) {
    const month = parts[0].padStart(2, "0");
    const day = parts[1].padStart(2, "0");
    const year = parts[2].length === 2 ? `20${parts[2]}` : parts[2];
    return `${year}-${month}-${day}`;
  }
  return "";
}

function fromInputDate(inputDate: string): string {
  if (!inputDate) return "";
  const [year, month, day] = inputDate.split("-");
  return `${month}/${day}/${year}`;
}
